import { Formik } from "formik";
import _ from "lodash";
import { useEffect, useMemo, useState } from "react";
import { Col, Row, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumbs from "../../components/Breadcrumbs";
import Button from "../../components/button";
import FileUpload from "../../components/FileUpload";
import Input from "../../components/input";
import Select from "../../components/Select";
import { findCollectionByKeys, normalizeOptions } from "../../utils/helper";
import { fetchIssueEquipmentDropdown, fetchIssueTyresDropdown, fetchIssueVehiclesDropdown, getServiceEntryDropdown } from "../../store/Slices/IssuesSlice";
import { getUserDropdown } from "../../store/Slices/UserSlice";
import {
  clearServiceEntryByVehicleDropdown,
  financialApprovalStatus,
  getServiceEntryByVehicleDropdown,
  getVendorsDropdown,
  getWorkOrderById,
  getWorkOrderTagsDropdown,
  invoiceWorkOrderPdf,
  operationalApprovalStatus,
  updateWorkOrder,
} from "../../store/Slices/WorkOrderSlice";
import { AppMainModules, MaintinaceSubModules, WorkOrdersPermission } from "../../utils/app-constant";
import { workOrderStatusOptions } from "../../utils/Dropdowns";
import { formatDate, hasPermission } from "../../utils/helper";
import { WorkOrderValidationSchema } from "../../utils/Schema";
import {
  closedIcon,
  exportDataIcon,
  inProgressIcon,
  notificationIcon,
  openIcon,
  pendingIcon,
  resolvedIcon,
} from "../../utils/svgs";
import { errorNotify, successNotify } from "../../utils/toast";
import WorkHistory from "./WorkHistory";

const AssetSourceTypeEnum = {
  Vehicle: "vehicle",
  Equipment: "equipment",
  Tyre: "tyre",
};

const assetTypeOptions = [
  { label: "Vehicle", value: AssetSourceTypeEnum.Vehicle },
  { label: "Equipment", value: AssetSourceTypeEnum.Equipment },
  { label: "Tyre", value: AssetSourceTypeEnum.Tyre },
];

const getPayloadId = (value) => {
  if (value === "" || value === null || value === undefined) {
    return value;
  }

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : value;
};

const getWorkOrderAssetType = (workOrder) => {
  const normalizedAssetType = String(workOrder?.asset_type || "").trim().toLowerCase();
  if (Object.values(AssetSourceTypeEnum).includes(normalizedAssetType)) {
    return normalizedAssetType;
  }

  if (workOrder?.vehicle || workOrder?.vehicle_id || workOrder?.asset_id) {
    return AssetSourceTypeEnum.Vehicle;
  }

  if (workOrder?.equipment || workOrder?.equipment_id) {
    return AssetSourceTypeEnum.Equipment;
  }

  if (workOrder?.tyre || workOrder?.tyre_id) {
    return AssetSourceTypeEnum.Tyre;
  }

  return "";
};

const getWorkOrderAssetId = (workOrder) => {
  const candidates = [
    workOrder?.asset_id,
    workOrder?.vehicle_id,
    workOrder?.equipment_id,
    workOrder?.tyre_id,
    workOrder?.vehicle?.id,
    workOrder?.vehicle?.vehicle_id,
    workOrder?.equipment?.id,
    workOrder?.equipment?.equipment_id,
    workOrder?.tyre?.id,
    workOrder?.tyre?.tyre_id,
    workOrder?.tyre?.system_id,
  ];

  for (const candidate of candidates) {
    if (candidate === "" || candidate === null || candidate === undefined) {
      continue;
    }

    return candidate;
  }

  return "";
};

const getWorkOrderAssetLabel = (workOrder, assetType) => {
  if (assetType === AssetSourceTypeEnum.Vehicle) {
    return (
      workOrder?.vehicle?.registration_number ||
      workOrder?.vehicle?.registration_no ||
      workOrder?.vehicle?.vehicle_number ||
      workOrder?.vehicle?.asset_id ||
      workOrder?.asset_id ||
      ""
    );
  }

  if (assetType === AssetSourceTypeEnum.Equipment) {
    return (
      workOrder?.equipment?.equipment_id ||
      workOrder?.equipment?.title ||
      workOrder?.equipment?.name ||
      workOrder?.equipment?.serial_number ||
      workOrder?.asset_id ||
      ""
    );
  }

  if (assetType === AssetSourceTypeEnum.Tyre) {
    return (
      workOrder?.tyre?.tyre_id ||
      workOrder?.tyre?.serial_number ||
      workOrder?.tyre?.system_id ||
      workOrder?.tyre?.name ||
      workOrder?.asset_id ||
      ""
    );
  }

  return "";
};

const WorkOrderDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch();
  const userDetails = JSON.parse(
    localStorage.getItem("fms-maintenance-details")
  );

  const { userDropdownLoading, userDropdownData } = useSelector(
    (state) => state.users
  );
  const {
    issueVehiclesDropdownData,
    issueVehiclesDropdownLoading,
    issueEquipmentDropdownData,
    issueEquipmentDropdownLoading,
    issueTyresDropdownData,
    issueTyresDropdownLoading,
  } = useSelector((state) => state.issues);
  const {
    getVendorsDropdownData,
    getVendorsDropdownLoading,
    getServiceEntryByVehicleDropdownData,
    getServiceEntryByVehicleDropdownLoading,
  } = useSelector((state) => state.workOrder);

  const {
    workOrderByIdData,
    workOrderByIdLoading,
    updateWorkOrderLoading,
    getWorkOrderTagsDropdownData,
    getWorkOrderTagsDropdownLoading,
    invoiceWorkOrderPdfLoading
  } = useSelector((state) => state.workOrder);

  const [uploadedFiles, setUploadedFiles] = useState([]);

  const [discountPercent, setDiscountPercent] = useState(
    parseFloat(workOrderByIdData?.discount_amount) || 0
  );
  const [taxPercent, setTaxPercent] = useState(
    parseFloat(workOrderByIdData?.tax_amount) || 0
  );
  const [linkIssues, setLinkIssues] = useState([]);
  const [summaryCost, setSummaryCost] = useState([]);

  const canUpdate = hasPermission({ permissionSlug: WorkOrdersPermission.Update, menuSlug: MaintinaceSubModules.WorkOrders, moduleSlug: AppMainModules.maintenance });
  const canFinanceApproval = hasPermission({ permissionSlug: WorkOrdersPermission.FinanceApproval, menuSlug: MaintinaceSubModules.WorkOrders, moduleSlug: AppMainModules.maintenance });
  const canOperationalApproval = hasPermission({ permissionSlug: WorkOrdersPermission.OperationsApproval, menuSlug: MaintinaceSubModules.WorkOrders, moduleSlug: AppMainModules.maintenance });

  useEffect(() => {
    dispatch(clearServiceEntryByVehicleDropdown());
    dispatch(getUserDropdown());
    dispatch(fetchIssueVehiclesDropdown());
    dispatch(fetchIssueEquipmentDropdown());
    dispatch(fetchIssueTyresDropdown());
    dispatch(getServiceEntryDropdown());
    dispatch(getVendorsDropdown());
    dispatch(getWorkOrderTagsDropdown());
    return () => {
      dispatch(clearServiceEntryByVehicleDropdown());
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(getWorkOrderById(id));
  }, [dispatch, id]);

  useEffect(() => {
    const assetType = getWorkOrderAssetType(workOrderByIdData);
    if (assetType && workOrderByIdData?.asset_id) {
      dispatch(getServiceEntryByVehicleDropdown({
        assetType,
        assetId: workOrderByIdData.asset_id,
      }));
      return;
    }

    dispatch(clearServiceEntryByVehicleDropdown());
  }, [dispatch, workOrderByIdData]);

  useEffect(() => {
    if (workOrderByIdData) {
      const allIssues = (workOrderByIdData.services || []).flatMap(
        (service) => service.service_issues || []
      );
      setLinkIssues(allIssues);

      setSummaryCost(workOrderByIdData.services || []);

      setUploadedFiles(
        (workOrderByIdData.images || []).map((img) => ({
          fileName: img.file_path,
          originalName: img.file_path,
          previewUrl: null,
          backend: true,
          id: img.id
        }))
      );
    }
  }, [workOrderByIdData]);

  const userOptions =
    userDropdownData &&
    userDropdownData.map((user) => ({
      value: user.value,
      label: user.label,
      image: user.image,
      showIcon: true,
    }));

  const vehicleOptions = useMemo(() => {
    const source = Array.isArray(issueVehiclesDropdownData)
      ? issueVehiclesDropdownData
      : findCollectionByKeys(issueVehiclesDropdownData, [
        "vehicles",
        "vehicle",
        "vehiclesdropdown",
        "vehicledropdown",
        "vehiclelist",
      ]);

    return normalizeOptions(source, {
      valueKeys: ["value", "id", "vehicle_id", "ref_id"],
      labelKeys: ["label", "registration_number", "registration_no", "vehicle_registration_number", "name", "title"],
      defaultLabelPrefix: "Vehicle",
    });
  }, [issueVehiclesDropdownData]);

  const vendorOptions =
    getVendorsDropdownData &&
    getVendorsDropdownData.map((vendor) => ({
      value: vendor.value,
      label: vendor.title,
    }));

  const equipmentOptions = useMemo(() => {
    const source = Array.isArray(issueEquipmentDropdownData)
      ? issueEquipmentDropdownData
      : findCollectionByKeys(issueEquipmentDropdownData, [
        "equipments",
        "equipmentdropdown",
        "equipment",
        "equipmentlist",
      ]);

    return normalizeOptions(source, {
      valueKeys: ["value", "id", "equipment_id"],
      labelKeys: ["label", "equipment_id", "title", "name", "serial_number", "registration_number"],
      defaultLabelPrefix: "Equipment",
    });
  }, [issueEquipmentDropdownData]);

  const tyreOptions = useMemo(() => {
    const source = Array.isArray(issueTyresDropdownData)
      ? issueTyresDropdownData
      : findCollectionByKeys(issueTyresDropdownData, [
        "tyres",
        "tyre",
        "tyresdropdown",
        "tyredropdown",
        "tyrelist",
      ]);

    return normalizeOptions(source, {
      valueKeys: ["value", "id", "tyre_id", "system_id"],
      labelKeys: ["label", "tyre_id", "serial_number", "system_id", "name", "title"],
      defaultLabelPrefix: "Tyre",
    });
  }, [issueTyresDropdownData]);

  const serviceEntryOptions = (getServiceEntryByVehicleDropdownData || []).map((service) => ({
      value: service.id,
      label: service.service_id,
      service_issues: service.service_issues,
      services: service,
    }));

  const tagsOptions =
    getWorkOrderTagsDropdownData &&
    getWorkOrderTagsDropdownData.map((tag) => ({
      value: tag?.tag_name,
      label: tag?.tag_name,
    }));

  const repairClassOptions = [
    { value: "scheduled", label: "Scheduled" },
    { value: "unscheduled", label: "Unscheduled" },
  ];

  const statusHandler = async (status, isAdmin = false) => {
    try {

      if (isAdmin) {
        await dispatch(
          operationalApprovalStatus({
            work_order_id: parseInt(id),
            approval_status: status,
          })
        ).unwrap();

        successNotify("Status updated successfully");
        navigate("/work-orders");
        return;
      }

      if (workOrderByIdData?.operation_status === "pending") {
        await dispatch(
          operationalApprovalStatus({
            work_order_id: parseInt(id),
            approval_status: status,
          })
        ).unwrap();

        successNotify("Status updated successfully");
        navigate("/work-orders");
        return;
      }
      // dispatch(getWorkOrderById(id));
    } catch (error) {
      errorNotify(error);
    }
  };

  const costSummary = (() => {
    if (!summaryCost.length)
      return { cost_parts: 0, cost_labor: 0, sub_total: 0 };
    const cost_parts = summaryCost.reduce(
      (sum, s) => sum + parseFloat(s.cost_parts || 0),
      0
    );
    const cost_labor = summaryCost.reduce(
      (sum, s) => sum + parseFloat(s.cost_labor || 0),
      0
    );
    const sub_total = cost_parts + cost_labor;
    return {
      cost_parts: cost_parts.toFixed(2),
      cost_labor: cost_labor.toFixed(2),
      sub_total: sub_total.toFixed(2),
    };
  })();

  const calculateTotal = () => {
    const subTotal = parseFloat(costSummary.sub_total);
    const discount = parseFloat(discountPercent) || 0;
    const tax = 0;
    const discountAmount = subTotal * (discount / 100);
    const taxAmount = subTotal * (tax / 100);
    return (subTotal + taxAmount - discountAmount).toFixed(2);
  };

  const initialValues = {
    asset_type: getWorkOrderAssetType(workOrderByIdData),
    asset_id: getPayloadId(getWorkOrderAssetId(workOrderByIdData)),
    reported_by: workOrderByIdData?.issued_by ?? "",
    assigned_to: workOrderByIdData?.assigned_to ?? "",
    repair_class: workOrderByIdData?.repair_class ?? "",
    status: workOrderByIdData?.status ?? "",
    issued_by: workOrderByIdData?.issued_by ?? "",
    reported_at: workOrderByIdData?.issued_at
      ? workOrderByIdData.issued_at.slice(0, 16)
      : "",
    tags:
      workOrderByIdData?.tags?.map((tag) => ({
        value: tag.tag_name,
        label: tag.tag_name,
      })) || [],
    files: [],
    link_service_ids: workOrderByIdData?.services?.map((s) => s.id) || [],
    invoice_no: workOrderByIdData?.invoice_no ?? "",
    vendor_id: workOrderByIdData?.vendor_id ?? "",
    issued_at: workOrderByIdData?.issued_at
      ? workOrderByIdData.issued_at.slice(0, 10)
      : "",
    scheduled_start_at: workOrderByIdData?.scheduled_start_at
      ? workOrderByIdData.scheduled_start_at.slice(0, 10)
      : "",
    expected_completion_at: workOrderByIdData?.expected_completion_at
      ? workOrderByIdData.expected_completion_at.slice(0, 10)
      : "",
    finance_status: workOrderByIdData?.finance_status ?? "",
    operation_status: workOrderByIdData?.operation_status ?? "",
    finance_approval_by: workOrderByIdData?.finance_approval_by ?? "",
    operation_approval_by: workOrderByIdData?.operation_approval_by ?? "",
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const changedKeys = Object.keys(values).filter((key) => {
        return !_.isEqual(values[key], initialValues[key]);
      });

      const hasNewFiles = uploadedFiles.some((file) => file.previewUrl);

      if (hasNewFiles && !changedKeys.includes("files")) {
        changedKeys.push("files");
      }

      // Validate discount/tax percent
      if (discountPercent < 0 || discountPercent > 100) {
        errorNotify("Discount must be between 0 and 100%");
        return;
      }
      if (taxPercent < 0 || taxPercent > 100) {
        errorNotify("Tax must be between 0 and 100%");
        return;
      }

      const workOrderData = {
        asset_id: getPayloadId(values.asset_id),
        work_order_id: parseInt(id),
        asset_type: values.asset_type,
        repair_class: values.repair_class,
        status: values.status,
        issued_at: values.issued_at,
        issued_by: parseInt(values.issued_by),
        scheduled_start_at: values.scheduled_start_at,
        expected_completion_at: values.expected_completion_at,
        assigned_to: parseInt(values.assigned_to),
        vendor_id: values.vendor_id ? parseInt(values.vendor_id) : null,
        finance_status: values.finance_status,
        operation_status: values.operation_status,
        invoice_no: values.invoice_no,
        tags: values.tags.map((tag) => tag.value || tag),
        files: uploadedFiles.map((file) => file.fileName),
        service_ids: values.link_service_ids,
        updated_keys: changedKeys,
      };

      await dispatch(updateWorkOrder(workOrderData)).unwrap();
      successNotify("Work Order Updated successfully");
      resetForm();
      navigate("/work-orders");
    } catch (error) {
      errorNotify(error);
    } finally {
      setSubmitting(false);
    }
  };

  if (workOrderByIdLoading) {
    return (
      <div className="loading_spinner">
        <Spinner animation="grow" />
      </div>
    );
  }

  const exportPdfHandler = () => {
    const data = {
      work_order_id: id,
    };
    dispatch(invoiceWorkOrderPdf(data)).unwrap()
      .then((response) => {
        const fileUrl = `${process.env.REACT_APP_API_FILES_URL}${response.data}`;
        const link = document.createElement("a");
        link.href = fileUrl;
        link.target = "_blank";
        link.setAttribute("download", `work-order.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }).catch((error) => {
        errorNotify(error);
      })

  };

  return (
    <div>
      <Breadcrumbs values={["Work Order", "Overview", "Work Order Detail"]} />

      <Formik
        initialValues={initialValues}
        validationSchema={WorkOrderValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          isSubmitting,
          submitForm,
        }) => {
          const selectedAssetType = values.asset_type || "";
          const currentAssetOptions = selectedAssetType === AssetSourceTypeEnum.Vehicle
            ? vehicleOptions
            : selectedAssetType === AssetSourceTypeEnum.Equipment
              ? equipmentOptions
              : selectedAssetType === AssetSourceTypeEnum.Tyre
                ? tyreOptions
                : [];
          const currentAssetLoading = selectedAssetType === AssetSourceTypeEnum.Vehicle
            ? issueVehiclesDropdownLoading
            : selectedAssetType === AssetSourceTypeEnum.Equipment
              ? issueEquipmentDropdownLoading
              : selectedAssetType === AssetSourceTypeEnum.Tyre
                ? issueTyresDropdownLoading
                : false;
          const currentAssetLabel = selectedAssetType === AssetSourceTypeEnum.Vehicle
            ? "Vehicle"
            : selectedAssetType === AssetSourceTypeEnum.Equipment
              ? "Equipment"
              : selectedAssetType === AssetSourceTypeEnum.Tyre
                ? "Tyre"
                : "Asset";
          const currentAssetPlaceholder = selectedAssetType
            ? `Select ${currentAssetLabel}`
            : "Select Asset Type First";
          const canLinkServiceEntries = Boolean(selectedAssetType && values.asset_id);
          const fallbackSelectedAssetOption =
            values.asset_id !== "" && values.asset_id !== null && values.asset_id !== undefined
              ? {
                value: values.asset_id,
                label: getWorkOrderAssetLabel(workOrderByIdData, selectedAssetType) || `${currentAssetLabel} ${values.asset_id}`,
              }
              : null;
          const selectedAssetValue =
            currentAssetOptions.find(
              (option) => String(option.value) === String(values.asset_id)
            ) ||
            fallbackSelectedAssetOption ||
            null;

          return (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submitForm();
            }}
          >
            <div className="access_view">
              <h2>Work Order Details</h2>
              <div className="service_entry_btn">
                <div className="services_buttons">
                  <div>
                    <button disabled={invoiceWorkOrderPdfLoading} type="button" onClick={exportPdfHandler}>
                      {
                        invoiceWorkOrderPdfLoading ? "Exporting..." : `Export PDF`
                      }
                    </button>
                  </div>
                </div>

                <Button
                  cancelBtn={() => {
                    navigate(-1);
                  }}
                  loading={isSubmitting || updateWorkOrderLoading}
                  disabled={isSubmitting || updateWorkOrderLoading}
                  submitText="Save Details"
                  type="submit"
                  shouldRow={false}
                  buttonExtraCss="save_issue_details_btn"
                  isShowSubmitBtn={canUpdate}
                />
              </div>
            </div>

            <div className="service_entry_main">
              <div className="container-fluid">
                <div className="row">
                  <div className="col-md-8">
                    <div className="service_entry_details">
                      <Row>
                        <Col md={6}>
                          <h6>Main Information</h6>
                          <p>
                            Comprehensive information for this work order
                            record.
                          </p>
                        </Col>
                        <Col md={6}>
                          {userDetails?.my_role?.slug !== "super-admin" && (
                            <>
                              {
                                (canOperationalApproval &&
                                  workOrderByIdData?.operation_status ===
                                  "pending") ? (
                                  <>
                                    <div className="work_order_status">
                                      <div>
                                        <h5>Work Order Status Update</h5>
                                        <p>
                                          Please approve or reject the work order.
                                        </p>
                                      </div>
                                      <div className="d-flex align-items-center gap-2">
                                        <button
                                          type="button"
                                          onClick={() =>
                                            statusHandler("approved")
                                          }
                                        >
                                          {resolvedIcon} Approve
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            statusHandler("rejected")
                                          }
                                        >
                                          {closedIcon} Reject
                                        </button>
                                      </div>
                                    </div>
                                  </>
                                ) : null}
                            </>
                          )}

                          {userDetails?.my_role?.slug === "super-admin" && (
                            <>
                              {canOperationalApproval &&
                                workOrderByIdData?.operation_status ===
                                "pending" ? (
                                <>
                                  <div className="work_order_status">
                                    <div>
                                      <h5>Work Order Status Update</h5>
                                      <p>
                                        Please approve or reject the work order.
                                      </p>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                      <button
                                        type="button"
                                        onClick={() =>
                                          statusHandler("approved", true)
                                        }
                                      >
                                        {resolvedIcon} Approve
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          statusHandler("rejected", true)
                                        }
                                      >
                                        {closedIcon} Reject
                                      </button>
                                    </div>
                                  </div>
                                </>
                              ) : null}
                            </>
                          )}
                        </Col>
                        <Col md={6}>
                          <Select
                            label="Assets Type"
                            placeholder="Select Asset Type"
                            options={assetTypeOptions}
                            loading={false}
                            name="asset_type"
                            value={assetTypeOptions.find(
                              (option) => option.value === values.asset_type
                            ) || null}
                            onChange={() => {}}
                            onBlur={handleBlur}
                            error={errors.asset_type}
                            touched={touched.asset_type}
                            isSearch={false}
                            isDisabled={true}
                          />
                        </Col>
                        <Col md={6}>
                          <Select
                            label={currentAssetLabel}
                            placeholder={currentAssetPlaceholder}
                            options={currentAssetOptions}
                            loading={currentAssetLoading}
                            name="asset_id"
                            value={selectedAssetValue}
                            onChange={() => {}}
                            onBlur={handleBlur}
                            error={errors.asset_id}
                            touched={touched.asset_id}
                            isSearch={false}
                            isDisabled={true}
                          />
                        </Col>
                        <Col md={6}>
                          <Select
                            label="Status"
                            placeholder="Select Status"
                            options={workOrderStatusOptions}
                            loading={false}
                            name="status"
                            value={workOrderStatusOptions.find(
                              (option) => option.value === values.status
                            )}
                            onChange={(selectedOption) =>
                              setFieldValue(
                                "status",
                                selectedOption?.value || ""
                              )
                            }
                            onBlur={handleBlur}
                            error={errors.status}
                            touched={touched.status}
                            isSearch={false}
                            isDisabled={true}
                          />
                        </Col>
                        <Col md={6}>
                          <Select
                            label="Repair Priority Class"
                            placeholder="Select Repair Priority Class"
                            options={repairClassOptions}
                            loading={false}
                            name="repair_class"
                            value={repairClassOptions.find(
                              (option) => option.value === values.repair_class
                            )}
                            onChange={(selectedOption) =>
                              setFieldValue(
                                "repair_class",
                                selectedOption?.value || ""
                              )
                            }
                            onBlur={handleBlur}
                            error={errors.repair_class}
                            touched={touched.repair_class}
                            isSearch={false}
                          />
                        </Col>

                        <Col md={6}>
                          <Select
                            label="Issued By"
                            placeholder="Please Select"
                            options={userOptions}
                            loading={userDropdownLoading}
                            name="issued_by"
                            value={userOptions.find(
                              (option) => option.value === values.issued_by
                            )}
                            onChange={(selectedOption) =>
                              setFieldValue(
                                "issued_by",
                                selectedOption?.value || ""
                              )
                            }
                            onBlur={handleBlur}
                            error={errors.issued_by}
                            touched={touched.issued_by}
                            isSearch={false}
                            isDisabled={true}
                          />
                        </Col>
                        <Col md={6}>
                          <Input
                            label="Issued At"
                            placeholder=""
                            type="date"
                            name="issued_at"
                            value={values.issued_at}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={errors.issued_at}
                            touched={touched.issued_at}
                            disabled={true}
                          />
                        </Col>
                        <Col md={6}>
                          <Input
                            label="Scheduled Start Date"
                            placeholder=""
                            type="date"
                            name="scheduled_start_at"
                            value={values.scheduled_start_at}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={errors.scheduled_start_at}
                            touched={touched.scheduled_start_at}
                          />
                        </Col>
                        <Col md={6}>
                          <Input
                            label="Expected Completion Date"
                            placeholder=""
                            type="date"
                            name="expected_completion_at"
                            value={values.expected_completion_at}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={errors.expected_completion_at}
                            touched={touched.expected_completion_at}
                          />
                        </Col>
                        <Col md={6}>
                          <Input
                            label="Invoice Number"
                            placeholder="Enter Invoice Number"
                            type="text"
                            name="invoice_no"
                            value={values.invoice_no}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={errors.invoice_no}
                            touched={touched.invoice_no}
                          />
                        </Col>
                        <Col md={6}>
                          <Select
                            label="Vendor"
                            placeholder="Select Vendor"
                            options={vendorOptions}
                            loading={getVendorsDropdownLoading}
                            name="vendor_id"
                            value={vendorOptions.find(
                              (option) => option.value === values.vendor_id
                            )}
                            onChange={(option) =>
                              setFieldValue("vendor_id", option?.value || "")
                            }
                            onBlur={handleBlur}
                            error={errors.vendor_id}
                            touched={touched.vendor_id}
                            isSearch={false}
                          />
                        </Col>
                        <Col md={6}>
                          <Select
                            label="Assignee"
                            placeholder="Please Select"
                            options={userOptions}
                            loading={userDropdownLoading}
                            name="assigned_to"
                            value={userOptions.find(
                              (option) => option.value === values.assigned_to
                            )}
                            onChange={(option) =>
                              setFieldValue("assigned_to", option?.value || "")
                            }
                            onBlur={handleBlur}
                            error={errors.assigned_to}
                            touched={touched.assigned_to}
                            isSearch={false}
                          />
                        </Col>

                        <Col md={12}>
                          <Select
                            label="Tags"
                            placeholder="Add Tags"
                            options={tagsOptions}
                            loading={getWorkOrderTagsDropdownLoading}
                            name="tags"
                            value={values.tags}
                            onChange={(selectedOptions) =>
                              setFieldValue("tags", selectedOptions || [])
                            }
                            onBlur={handleBlur}
                            isMulti={true}
                            isCreatable={true}
                            error={
                              errors.tags && typeof errors.tags === "string"
                                ? errors.tags
                                : null
                            }
                            touched={touched.tags}
                          />
                        </Col>

                        <Col md={6}>
                          <Input
                            label="Finance Approval Status"
                            placeholder="Select Finance Approval Status"
                            type="text"
                            name="finance_status"
                            value={values.finance_status}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={errors.finance_status}
                            touched={touched.finance_status}
                            disabled={true}
                            showIcon={notificationIcon}
                          />
                        </Col>

                        <Col md={6}>
                          <Input
                            label="Operations Approval Status"
                            placeholder="Select Operations Approval Status"
                            type="text"
                            name="operation_status"
                            value={values.operation_status}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={errors.operation_status}
                            touched={touched.operation_status}
                            disabled={true}
                            showIcon={notificationIcon}
                          />
                        </Col>
                      </Row>
                    </div>

                    <div
                      className="service_entry_details issue_tracking_main"
                      style={{ marginTop: "20px" }}
                    >
                      <h6>Linked Issues</h6>
                      <p>Link Issues related to this Work Order</p>

                      <div className="issue_tracking_lists">
                        {linkIssues?.length > 0 ? (
                          linkIssues?.map((issue) => {
                            return (
                              <div
                                key={issue.id}
                                className="issue_tracking_item"
                              >
                                <div className="d-flex flex-column align-items-start">
                                  {issue.summery ||
                                    issue.description ||
                                    "No summary available"}
                                  <span>
                                    Issue ID: {issue.service_issue_id} -{" "}
                                    {formatDate(issue.reported_at)}
                                  </span>
                                </div>
                                <div
                                  className={`d-flex align-items-center gap-1 status ${issue?.status}`}
                                  style={{
                                    cursor: "pointer",
                                    textTransform: "capitalize",
                                  }}
                                >
                                  {issue?.status === "pending"
                                    ? pendingIcon
                                    : issue?.status === "open"
                                      ? openIcon
                                      : issue?.status === "closed"
                                        ? closedIcon
                                        : issue?.status === "resolved"
                                          ? resolvedIcon
                                          : issue?.status === "completed"
                                            ? resolvedIcon
                                            : issue?.status === "in_progress"
                                              ? inProgressIcon
                                              : null}
                                  {issue?.status}
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <div className="no_issues">
                            <p>No Linked Issues.</p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div
                      className="service_entry_details"
                      style={{ marginTop: "20px" }}
                    >
                      <h6>Service Entry</h6>
                      <p>Link Service Entries related to this Work Order.</p>

                      <Row>
                        <Col md={12}>
                          <Select
                            label="Link Service Entry"
                            placeholder={selectedAssetType ? `Please Select Link Service Entry for ${currentAssetLabel}` : "Select Asset Type First"}
                            options={selectedAssetType ? serviceEntryOptions : []}
                            loading={canLinkServiceEntries ? getServiceEntryByVehicleDropdownLoading : false}
                            name="link_service_ids"
                            value={(selectedAssetType ? serviceEntryOptions : []).filter((option) =>
                              values.link_service_ids.includes(option.value)
                            )}
                            onChange={(selectedOptions) => {
                              const selectedIds = selectedOptions
                                ? selectedOptions.map((opt) => opt.value)
                                : [];
                              setFieldValue("link_service_ids", selectedIds);

                              const selectedServices =
                                serviceEntryOptions.filter((opt) =>
                                  selectedIds.includes(opt.value)
                                );

                              const allIssues = selectedServices.flatMap(
                                (s) => s.service_issues || []
                              );
                              const allServices = selectedServices.flatMap(
                                (s) => s.services || []
                              );

                              setSummaryCost(allServices);
                              setLinkIssues(allIssues);
                            }}
                            onBlur={handleBlur}
                            error={errors.link_service_ids}
                            touched={touched.link_service_ids}
                            isSearch={false}
                            isMulti={true}
                            isDisabled={!canLinkServiceEntries}
                          />
                        </Col>
                      </Row>
                    </div>

                    <div
                      className="service_cost_summary_details mt-4"
                      style={{ position: "relative", top: "0" }}
                    >
                      <div className="service_cost_summary">
                        <h6>Cost Summary</h6>
                        <p>Financial breakdown of the service entries.</p>
                        <div className="subtotal">
                          <div>Subtotal (Calculated)</div>
                          <span>PKR {costSummary.sub_total}</span>
                        </div>
                        <div className="service_item">
                          <div>Parts</div>
                          <div>PKR {costSummary.cost_parts}</div>
                        </div>
                        <div className="service_item">
                          <div>Labor</div>
                          <div>PKR {costSummary.cost_labor}</div>
                        </div>
                        {/* <div className="service_item">
                          <div>Discount (%)</div>
                          <div>
                            <input
                              className='edit_input'
                              type="number"
                              min={0}
                              max={100}
                              value={discountPercent}
                              onChange={handleDiscountChange}
                              placeholder='0'
                              disabled
                            /> %
                          </div>
                        </div> */}
                        {/* <div className="service_item">
                          <div>Tax (%)</div>
                          <div>
                            <input
                              className='edit_input'
                              type="number"
                              min={0}
                              max={100}
                              value={taxPercent}
                              onChange={handleTaxChange}
                              placeholder='0'
                              disabled
                            /> %
                          </div>
                        </div> */}
                        <div className="total">
                          <div>Total</div>
                          <span>PKR {calculateTotal()}</span>
                        </div>
                      </div>
                    </div>

                    <div className="photo_document_section mb-3">
                      <h6>Photos & Documents</h6>
                      <p>
                        Upload relevant photos or documents for this service.
                      </p>
                      <FileUpload
                        destination="service-entry"
                        files={uploadedFiles}
                        setFiles={setUploadedFiles}
                        maxFileSize={10}
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <WorkHistory />
                  </div>
                </div>
              </div>
            </div>
          </form>
        );
        }}
      </Formik>
    </div>
  );
};

export default WorkOrderDetails;
